import { bbox, featureCollection, feature as turfFeature } from "@turf/turf";
import { useMemo } from "react";

import { MAP_SHAPE_ICON_IMAGE_IDS } from "~/modules/map/icons";
import { stripeFillPatternId } from "~/modules/map/util/imageLoaders";

import { ProjectDeliverablesMap_FeatureFragment } from "../../../fragments.generated";

type PointProperties = { color: string; iconId: string };
type LineProperties = { color: string };
type PolygonProperties = { color: string; fillPatternId: string };
type PlannerAreaProperties = { name: string };
type FeatureSpecies = { name: string; mapColor: string };

const NO_SPECIES_COLOR = "#9E9E9E";

export function usePreparedGeoJson(features: ProjectDeliverablesMap_FeatureFragment[]) {
    return useMemo(() => {
        const pointFeatures: GeoJSON.Feature<GeoJSON.Point | GeoJSON.MultiPoint, PointProperties>[] = [];
        const lineFeatures: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString, LineProperties>[] = [];
        const polygonFeatures: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, PolygonProperties>[] = [];
        const plannerAreaFeatures: GeoJSON.Feature<GeoJSON.Geometry, PlannerAreaProperties>[] = [];

        features.forEach(mapFeature => {
            const geometry = mapFeature.geometry as GeoJSON.Geometry;

            if (mapFeature.data.__typename === "GqlPlannerArea") {
                plannerAreaFeatures.push(turfFeature(geometry, { name: mapFeature.data.name }));
                return;
            }

            if (mapFeature.data.__typename !== "GqlEcologicalFunction") {
                return;
            }

            const { speciesList, type } = mapFeature.data;
            const color = determineFeatureColor(speciesList);

            if (geometry.type === "Point" || geometry.type === "MultiPoint") {
                pointFeatures.push(turfFeature(geometry, { color, iconId: MAP_SHAPE_ICON_IMAGE_IDS[type.mapIcon] }));
            } else if (geometry.type === "LineString" || geometry.type === "MultiLineString") {
                lineFeatures.push(turfFeature(geometry, { color }));
            } else if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
                polygonFeatures.push(turfFeature(geometry, { color, fillPatternId: stripeFillPatternId(color) }));
            }
        });

        return {
            points: featureCollection(pointFeatures),
            lines: featureCollection(lineFeatures),
            polygons: featureCollection(polygonFeatures),
            plannerAreas: featureCollection(plannerAreaFeatures),
            bounds: bbox(featureCollection(features.map(feature => turfFeature(feature.geometry)))),
        };
    }, [features]);
}

// A function can be linked to several species; it is drawn in the colour of the first species alphabetically.
function determineFeatureColor(speciesList: FeatureSpecies[]): string {
    if (speciesList.length === 0) {
        return NO_SPECIES_COLOR;
    }

    const [firstSpecies] = [...speciesList].sort((speciesA, speciesB) => speciesA.name.localeCompare(speciesB.name));

    return firstSpecies.mapColor;
}
